/**
 * Country-aware UI defaults synced from regional_settings (onboarding / Regional Settings).
 * Used by phone inputs, address forms, and legacy currency helpers.
 */
import i18n from '../i18n';

const FALLBACK_ISO = 'IN';

/** Local examples keyed by ISO 3166-1 alpha-2 (matches regional_settings.countryCode). */
const EXAMPLES_BY_ISO = {
  IN: {
    phoneLocal: '9876543210',
    addressLine1: '42, MG Road',
    postcode: '560001',
  },
  US: {
    phoneLocal: '2025551234',
    addressLine1: '123 Main Street',
    postcode: '10001',
  },
  GB: {
    phoneLocal: '7911123456',
    addressLine1: '10 High Street',
    postcode: 'SW1A 1AA',
  },
  AE: {
    phoneLocal: '501234567',
    addressLine1: 'Sheikh Zayed Road',
    postcode: '',
  },
  DE: {
    phoneLocal: '15123456789',
    addressLine1: 'Friedrichstraße 1',
    postcode: '10117',
  },
  FR: {
    phoneLocal: '612345678',
    addressLine1: '10 Rue de Rivoli',
    postcode: '75001',
  },
  NL: {
    phoneLocal: '612345678',
    addressLine1: 'Damrak 1',
    postcode: '1012 LG',
  },
  ES: {
    phoneLocal: '612345678',
    addressLine1: 'Calle Mayor 1',
    postcode: '28013',
  },
};

let state = {
  countryCode: '',
  countryName: '',
  phoneDialCode: '',
  phoneFormatHint: '',
  localeTag: 'en',
  currencyCode: '',
  currencySymbol: '',
  currencyDecimals: 2,
  currencySymbolBefore: true,
};

function resolvedIso() {
  const cc = (state.countryCode || '').trim().toUpperCase();
  return cc.length === 2 ? cc : FALLBACK_ISO;
}

function examplesForIso(iso) {
  return EXAMPLES_BY_ISO[iso] || EXAMPLES_BY_ISO[FALLBACK_ISO];
}

export function syncRegionalDefaults(next) {
  if (!next) return;
  state = {
    ...state,
    countryCode: next.countryCode ?? state.countryCode,
    countryName: next.countryName ?? state.countryName,
    phoneDialCode: next.phoneDialCode ?? state.phoneDialCode,
    phoneFormatHint: next.phoneFormatHint ?? state.phoneFormatHint,
    localeTag: next.localeTag ?? state.localeTag,
    currencyCode: next.currencyCode ?? state.currencyCode,
    currencySymbol: next.currencySymbol ?? state.currencySymbol,
    currencyDecimals: next.currencyDecimals ?? state.currencyDecimals,
    currencySymbolBefore: next.currencySymbolBefore ?? state.currencySymbolBefore,
  };
}

export function getRegionalDefaults() {
  const iso = resolvedIso();
  const examples = examplesForIso(iso);
  return {
    countryCode: iso,
    countryName: state.countryName || '',
    phoneDialCode: state.phoneDialCode || '',
    phoneFormatHint: state.phoneFormatHint || '',
    phoneLocalPlaceholder: examples.phoneLocal,
    addressLine1Placeholder: examples.addressLine1
      ? i18n.t('forms.address.egExample', {
          example: examples.addressLine1,
          defaultValue: 'e.g. {{example}}',
        })
      : i18n.t('forms.address.streetPlaceholder', { defaultValue: 'Street address' }),
    postcodePlaceholder: examples.postcode
      ? i18n.t('forms.address.egExample', {
          example: examples.postcode,
          defaultValue: 'e.g. {{example}}',
        })
      : i18n.t('forms.address.postcode', { defaultValue: 'Postcode' }),
    localeTag: state.localeTag || 'en',
    currencyCode: state.currencyCode || '',
    currencySymbol: state.currencySymbol || '',
    currencyDecimals: state.currencyDecimals ?? 2,
    currencySymbolBefore: state.currencySymbolBefore ?? true,
    isConfigured: Boolean(state.countryCode && state.currencyCode),
  };
}

/** ISO country for empty phone fields (from regional settings). */
export function getDefaultPhoneCountryIso() {
  return resolvedIso();
}

/** Display name for default address country dropdown. */
export function getDefaultCountryName() {
  return state.countryName || '';
}
